import { z } from 'zod';
export const mitigationDryingWorkspaceCapability = 'mitigation.drying-workspace.v1';
export const mitigationDryingReadingsCapability = 'mitigation.drying-readings.v1';
export const mitigationDryingSizingCapability = 'mitigation.drying-sizing.v1';
export const mitigationDryingIdSchema = z.string().uuid();
export const mitigationDryingReasonSchema = z.string().trim().min(10).max(500);
export const mitigationDryingDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year ?? 0, (month ?? 0) - 1, day ?? 0));
    return date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month &&
        date.getUTCDate() === day;
}, 'Date must be a real calendar date in YYYY-MM-DD form.');
export const mitigationDryingSourceReferenceSchema = z.strictObject({
    source: z.enum(['manual', 'scanning-media', 'import']),
    referenceId: z.string().trim().min(1).max(240).nullable(),
});
export const mitigationDryingPlanPositionSchema = z.strictObject({
    xFeetMillis: z.number().int().min(-10_000_000).max(10_000_000),
    yFeetMillis: z.number().int().min(-10_000_000).max(10_000_000),
    floorKey: z.string().trim().min(1).max(80).nullable(),
});
export const mitigationDryingMaterialTypeSchema = z.enum([
    'drywall',
    'hardwood',
    'concrete',
    'carpet',
    'plywood',
    'other',
]);
export const mitigationDryingWaterClassSchema = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
]);
export const mitigationDryingDehumidifierKindSchema = z.enum([
    'refrigerant',
    'lgr',
    'desiccant',
]);
export const mitigationDryingPointKindSchema = z.enum([
    'material-moisture',
    'affected-air',
    'unaffected-air',
    'exterior-air',
    'equipment-air',
]);
export const mitigationDryingSiteInputSchema = z.strictObject({
    name: z.string().trim().min(1).max(160),
    description: z.string().trim().min(1).max(1_000).nullable().default(null),
    sortOrder: z.number().int().min(0).max(100_000).default(0),
    sourceReference: mitigationDryingSourceReferenceSchema.default({
        source: 'manual',
        referenceId: null,
    }),
});
export const mitigationDryingChamberInputSchema = z.strictObject({
    siteId: mitigationDryingIdSchema,
    name: z.string().trim().min(1).max(160),
    waterClass: mitigationDryingWaterClassSchema.nullable().default(null),
    dehumidifierKind: mitigationDryingDehumidifierKindSchema.nullable().default(null),
    sortOrder: z.number().int().min(0).max(100_000).default(0),
    sourceReference: mitigationDryingSourceReferenceSchema.default({
        source: 'manual',
        referenceId: null,
    }),
});
const mitigationDryingDimensionHundredthsSchema = z.number().int().min(0).max(100_000_000);
export const mitigationDryingRoomInputSchema = z.strictObject({
    chamberId: mitigationDryingIdSchema,
    name: z.string().trim().min(1).max(160),
    materialType: mitigationDryingMaterialTypeSchema,
    dryGoalMillis: z.number().int().min(0).max(999_000).nullable().default(null),
    affectedFloorSquareFeetHundredths: mitigationDryingDimensionHundredthsSchema.default(0),
    ceilingHeightFeetHundredths: mitigationDryingDimensionHundredthsSchema.default(800),
    affectedWallLinearFeetHundredths: mitigationDryingDimensionHundredthsSchema.default(0),
    affectedCeilingSquareFeetHundredths: mitigationDryingDimensionHundredthsSchema.default(0),
    obstructionCount: z.number().int().min(0).max(10_000).default(0),
    sortOrder: z.number().int().min(0).max(100_000).default(0),
    sourceReference: mitigationDryingSourceReferenceSchema.default({
        source: 'manual',
        referenceId: null,
    }),
});
export const mitigationDryingPointInputSchema = z.strictObject({
    roomId: mitigationDryingIdSchema,
    label: z.string().trim().min(1).max(160),
    pointKind: mitigationDryingPointKindSchema,
    materialType: mitigationDryingMaterialTypeSchema.nullable().default(null),
    dryGoalMillis: z.number().int().min(0).max(999_000).nullable().default(null),
    sortOrder: z.number().int().min(0).max(100_000).default(0),
    planPosition: mitigationDryingPlanPositionSchema.nullable().default(null),
    sourceReference: mitigationDryingSourceReferenceSchema.default({
        source: 'manual',
        referenceId: null,
    }),
}).superRefine((value, context) => {
    if (value.pointKind === 'material-moisture' && value.materialType === null) {
        context.addIssue({
            code: 'custom',
            path: ['materialType'],
            message: 'A material-moisture point requires a material type.',
        });
    }
    if (value.pointKind !== 'material-moisture' && value.dryGoalMillis !== null) {
        context.addIssue({
            code: 'custom',
            path: ['dryGoalMillis'],
            message: 'Only material-moisture points carry a material dry goal.',
        });
    }
});
const mitigationDryingEntityBaseSchema = z.strictObject({
    tenantId: mitigationDryingIdSchema,
    id: mitigationDryingIdSchema,
    projectId: mitigationDryingIdSchema,
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: mitigationDryingIdSchema,
    updatedByMembershipId: mitigationDryingIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const mitigationDryingSiteSchema = mitigationDryingEntityBaseSchema.extend({
    entityType: z.literal('site'),
    data: mitigationDryingSiteInputSchema,
});
export const mitigationDryingChamberSchema = mitigationDryingEntityBaseSchema.extend({
    entityType: z.literal('chamber'),
    data: mitigationDryingChamberInputSchema,
});
export const mitigationDryingRoomSchema = mitigationDryingEntityBaseSchema.extend({
    entityType: z.literal('room'),
    data: mitigationDryingRoomInputSchema,
});
export const mitigationDryingPointSchema = mitigationDryingEntityBaseSchema.extend({
    entityType: z.literal('point'),
    data: mitigationDryingPointInputSchema,
});
export const mitigationDryingWorkspaceSchema = z.strictObject({
    contractVersion: z.literal(mitigationDryingWorkspaceCapability),
    projectId: mitigationDryingIdSchema,
    sites: z.array(mitigationDryingSiteSchema),
    chambers: z.array(mitigationDryingChamberSchema),
    rooms: z.array(mitigationDryingRoomSchema),
    points: z.array(mitigationDryingPointSchema),
});
const commandBase = {
    commandId: mitigationDryingIdSchema,
    entityId: mitigationDryingIdSchema,
    reason: mitigationDryingReasonSchema,
};
function createCommand(kind, data) {
    return z.strictObject({
        ...commandBase,
        kind: z.literal(kind),
        expectedRevision: z.null(),
        data,
    });
}
function updateCommand(kind, data) {
    return z.strictObject({
        ...commandBase,
        kind: z.literal(kind),
        expectedRevision: z.number().int().positive(),
        data,
    });
}
function stateCommand(kind) {
    return z.strictObject({
        ...commandBase,
        kind: z.literal(kind),
        expectedRevision: z.number().int().positive(),
        data: z.null(),
    });
}
export const mitigationDryingWorkspaceCommandSchema = z.discriminatedUnion('kind', [
    createCommand('site.create', mitigationDryingSiteInputSchema),
    updateCommand('site.update', mitigationDryingSiteInputSchema),
    stateCommand('site.archive'),
    stateCommand('site.restore'),
    createCommand('chamber.create', mitigationDryingChamberInputSchema),
    updateCommand('chamber.update', mitigationDryingChamberInputSchema),
    stateCommand('chamber.archive'),
    stateCommand('chamber.restore'),
    createCommand('room.create', mitigationDryingRoomInputSchema),
    updateCommand('room.update', mitigationDryingRoomInputSchema),
    stateCommand('room.archive'),
    stateCommand('room.restore'),
    createCommand('point.create', mitigationDryingPointInputSchema),
    updateCommand('point.update', mitigationDryingPointInputSchema),
    stateCommand('point.archive'),
    stateCommand('point.restore'),
]);
export const mitigationDryingWorkspaceCommandResponseSchema = z.strictObject({
    data: z.discriminatedUnion('entityType', [
        mitigationDryingSiteSchema,
        mitigationDryingChamberSchema,
        mitigationDryingRoomSchema,
        mitigationDryingPointSchema,
    ]),
    command: z.strictObject({
        commandId: mitigationDryingIdSchema,
        idempotent: z.boolean(),
    }),
});
export const mitigationDryingReadingTypeSchema = z.enum([
    'mc',
    'gpp',
    'rh',
    'temperature',
]);
export const mitigationDryingReadingUnitSchema = z.enum([
    'percent',
    'relative',
    'grains-per-pound',
    'fahrenheit',
]);
export const mitigationDryingRecordReadingCommandSchema = z.strictObject({
    kind: z.literal('record'),
    commandId: mitigationDryingIdSchema,
    readingId: mitigationDryingIdSchema,
    pointId: mitigationDryingIdSchema,
    workDate: mitigationDryingDateSchema,
    readingType: mitigationDryingReadingTypeSchema,
    unit: mitigationDryingReadingUnitSchema,
    valueMillis: z.number().int().min(-500_000).max(2_000_000),
    occurredAt: z.string().datetime({ offset: true }),
    source: z.enum(['manual', 'mobile', 'import']).default('manual'),
    sourceReference: z.string().trim().min(1).max(240).nullable().default(null),
    notes: z.string().trim().min(1).max(2_000).nullable().default(null),
    correctsReadingId: mitigationDryingIdSchema.nullable().default(null),
    reason: mitigationDryingReasonSchema,
}).superRefine((value, context) => {
    const validUnit = value.readingType === 'mc'
        ? value.unit === 'percent' || value.unit === 'relative'
        : value.readingType === 'gpp'
            ? value.unit === 'grains-per-pound'
            : value.readingType === 'rh'
                ? value.unit === 'percent'
                : value.unit === 'fahrenheit';
    if (!validUnit) {
        context.addIssue({
            code: 'custom',
            path: ['unit'],
            message: 'The reading unit does not match the reading type.',
        });
    }
});
export const mitigationDryingVoidReadingCommandSchema = z.strictObject({
    kind: z.literal('void'),
    commandId: mitigationDryingIdSchema,
    readingId: mitigationDryingIdSchema,
    reason: mitigationDryingReasonSchema,
});
export const mitigationDryingReadingSchema = z.strictObject({
    contractVersion: z.literal(mitigationDryingReadingsCapability),
    tenantId: mitigationDryingIdSchema,
    id: mitigationDryingIdSchema,
    projectId: mitigationDryingIdSchema,
    pointId: mitigationDryingIdSchema,
    workDate: mitigationDryingDateSchema,
    dayNumber: z.number().int().positive(),
    readingType: mitigationDryingReadingTypeSchema,
    unit: mitigationDryingReadingUnitSchema,
    valueMillis: z.number().int(),
    occurredAt: z.string().datetime({ offset: true }),
    receivedAt: z.string().datetime({ offset: true }),
    source: z.enum(['manual', 'mobile', 'import']),
    sourceReference: z.string().trim().min(1).max(240).nullable(),
    notes: z.string().trim().min(1).max(2_000).nullable(),
    correctsReadingId: mitigationDryingIdSchema.nullable(),
    void: z.strictObject({
        commandId: mitigationDryingIdSchema,
        reason: mitigationDryingReasonSchema,
        actorMembershipId: mitigationDryingIdSchema,
        voidedAt: z.string().datetime({ offset: true }),
    }).nullable(),
    createdByMembershipId: mitigationDryingIdSchema,
});
export const mitigationDryingReadingListQuerySchema = z.strictObject({
    pointId: mitigationDryingIdSchema.nullable().default(null),
    includeVoided: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().nonnegative().default(0),
});
export const mitigationDryingReadingsResponseSchema = z.strictObject({
    data: z.array(mitigationDryingReadingSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const mitigationDryingReadingResponseSchema = z.strictObject({
    data: mitigationDryingReadingSchema,
    command: z.strictObject({
        commandId: mitigationDryingIdSchema,
        idempotent: z.boolean(),
    }),
});
export const mitigationDryingSizingPolicySchema = z.strictObject({
    floorSquareFeetPerAirMover: z.record(z.enum(['1', '2', '3', '4']), z.number().int().min(10).max(500)),
    wallLinearFeetPerAirMover: z.number().int().min(1).max(100),
    cubicFeetPerPint: z.record(mitigationDryingDehumidifierKindSchema, z.record(z.enum(['1', '2', '3', '4']), z.number().int().min(1).max(500))),
    dehumidifierCapacityPintsPerDay: z.number().int().min(1).max(1_000),
});
export const mitigationDryingSizingInputSchema = z.strictObject({
    affectedFloorSquareFeetHundredths: mitigationDryingDimensionHundredthsSchema,
    ceilingHeightFeetHundredths: mitigationDryingDimensionHundredthsSchema,
    affectedWallLinearFeetHundredths: mitigationDryingDimensionHundredthsSchema,
    roomCount: z.number().int().min(1).max(10_000),
    waterClass: mitigationDryingWaterClassSchema,
    dehumidifierKind: mitigationDryingDehumidifierKindSchema,
    policy: mitigationDryingSizingPolicySchema,
});
export const mitigationDryingSizingResultSchema = z.strictObject({
    contractVersion: z.literal(mitigationDryingSizingCapability),
    inputs: mitigationDryingSizingInputSchema.omit({ policy: true }),
    policy: mitigationDryingSizingPolicySchema,
    wallMethodAirMovers: z.number().int().nonnegative(),
    floorMethodAirMovers: z.number().int().nonnegative(),
    recommendedAirMovers: z.number().int().nonnegative(),
    chamberCubicFeetHundredths: z.number().int().nonnegative(),
    requiredPintsPerDayHundredths: z.number().int().nonnegative(),
    recommendedDehumidifiers: z.number().int().nonnegative(),
    method: z.literal('initial-sizing-starting-point-field-adjustment-required'),
});
//# sourceMappingURL=mitigation-drying-workspace.contract.js.map